import { BORROW_STATUS_KEYS } from './pricingConstants';
import type {
    BorrowStatus as BorrowStatusKey,
    MinimumPriceInput,
    PoolPricingConfig,
    PoolPricingInput,
} from '@thinkalpha/locates-ws-client';

const isSameInput = <T extends PoolPricingInput>(
    a: T | undefined,
    b: T | undefined,
    valueKey: Exclude<keyof T, keyof PoolPricingInput>,
): boolean => {
    if (a === undefined || b === undefined) return a === b;
    return a.enabled === b.enabled && a[valueKey] === b[valueKey];
};

const isSameMinimum = (a: MinimumPriceInput | undefined, b: MinimumPriceInput | undefined): boolean => {
    // An absent input equals a disabled one with no floors: toggling the
    // minimum on and back off stages no real change (PLAT-13241). A
    // disabled input carrying floors still differs from an absent one —
    // the backend preserves persisted-but-disabled values.
    if ((a?.enabled ?? false) !== (b?.enabled ?? false)) return false;
    return BORROW_STATUS_KEYS.every((status: BorrowStatusKey) => a?.prices[status] === b?.prices[status]);
};

/**
 * Structural equality for pricing configs, used to compute the Info tab's
 * "unsaved changes" dirtiness and to route the simulator between the
 * saved-config and draft-config endpoints. Absent multiplier/quote-volume
 * inputs only equal absent inputs — a disabled input still differs from
 * no input, mirroring the backend's "persisted-but-disabled values are
 * preserved" contract. The minimum-price input relaxes that one notch:
 * see {@link isSameMinimum}.
 */
export function isSamePoolPricingConfig(a: PoolPricingConfig, b: PoolPricingConfig): boolean {
    if (a.hierarchy.length !== b.hierarchy.length) return false;
    if (a.hierarchy.some((method, index) => b.hierarchy[index] !== method)) return false;
    return (
        isSameMinimum(a.minimumPrice, b.minimumPrice) &&
        isSameInput(a.multiplier, b.multiplier, 'multiplier') &&
        isSameInput(a.quoteVolume, b.quoteVolume, 'percent')
    );
}
